import { ASSET_PERMISSIONS_BY_ACCESS_TYPE } from '@dtx-company/inter-app/src/components/FlowTeams/components/constants/asset-permissions.constants'
import {
  AssetPermission,
  CollaboratorAccess,
  PermissionName
} from '@dtx-company/inter-app/src/components/FlowTeams/types/asset-permissions.types'
import {
  CollaboratorsType,
  EntityPermissionsType,
  PermissionNameType,
  UseGetPermissionsProps,
  UseGetPermissionsReturnType
} from '@dtx-company/inter-app/src/types/permissions'
import { DEFAULT_PAGE_ID } from '@dtx-company/true-common/src/constants/page'
import { EntityTypes } from '@dtx-company/inter-app/src/redux/slices/modal'
import { PermissionsTypes } from '@dtx-company/inter-app/src/types/flowcode'
import { checkPageOwnership } from '@dtx-company/inter-app/src/redux/slices/pageEditor/getPageObj'
import {
  getBatchPermissions,
  getDirectoryPermissions,
  getNameInitials,
  getPagePermissions,
  getPermissionsName,
  getRandomBackgroundImage
} from '../utils/permissions'
import {
  getFlowcodeSubdomainPermissions,
  getFlowpageSubdomainPermissions
} from '../components/Domains/DomainsV2/hooks/subdomainPermissions.hooks'
import { getPageTemplatePermissions } from '@dtx-company/inter-app/src/utils/permissions/pageTemplates'
import { permissionsNames } from '../constants/permissions'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCurrentPage } from '@dtx-company/inter-app/src/redux/slices/utils/currentPage'
import { useIsDemo } from '@app/page/src/utils/ContextUtil'
import { useIsMounted } from '@dtx-company/true-common/src/hooks/useIsMounted'

interface OwnerData {
  entityPermissions: EntityPermissionsType[]
  ownerFullName: string
  ownerEntityId: string
  isOwner: boolean
}

const getSelectedCollborators = (
  entityPermissions: EntityPermissionsType[]
): CollaboratorsType[] => {
  if (entityPermissions) {
    return entityPermissions.map(item => {
      const permissionName = item.permissionName as PermissionNameType
      return {
        name: item.entityName,
        entityId: item.entityId,
        entityType: item.entityType,
        icon: getRandomBackgroundImage(),
        access: permissionsNames[permissionName],
        initials: getNameInitials(item.entityName)
      }
    })
  }
  return []
}

export function useGetPermissions({
  batchId,
  directoryId,
  isBatch,
  pageId,
  shouldFetch = true,
  subdomainId,
  pageTemplateId
}: UseGetPermissionsProps): UseGetPermissionsReturnType {
  const [existingCollaborators, setExistingCollaborators] = useState<CollaboratorsType[]>([])
  const [hasOrgEntity, setHasOrgEntity] = useState(false)
  const [orgEntityAccess, setOrgEntityAccess] = useState<PermissionsTypes>()
  const isMounted = useIsMounted()
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState(true)
  const { token } = useAuthState()
  const [isPageUnpublished, setIsPageUnpublished] = useState<boolean>(
    Boolean(pageId && pageId === DEFAULT_PAGE_ID)
  )
  const [{ isOwner, ownerEntityId, ownerFullName, entityPermissions }, setOwnerData] =
    useState<OwnerData>({
      ownerFullName: '',
      ownerEntityId: '',
      isOwner: false,
      entityPermissions: []
    })
  const { jwt } = useAuthState()
  const myEntityId = jwt?.ithacaId
  const isDemo = useIsDemo()
  const thisPage = useCurrentPage(pageId)
  const fetchPermissions = useCallback(async (): Promise<void> => {
    let existingCollaborators: CollaboratorsType[] = []
    let entityPermissions: EntityPermissionsType[] = []
    let isOwner = false
    let ownerFullName = ''
    let ownerEntityId = ''
    try {
      setLoading(true)
      setError('')
      // TODO - extract below into separate functions - DF
      if (pageId && !isDemo) {
        if (pageId === DEFAULT_PAGE_ID) {
          isOwner = true
          setIsPageUnpublished(true)
        } else {
          setIsPageUnpublished(false)
          const { getAllSharedPagePermissions } = await getPagePermissions({ pageId })
          entityPermissions = getAllSharedPagePermissions
          existingCollaborators = [...getSelectedCollborators(getAllSharedPagePermissions)]
          isOwner = await checkPageOwnership({ pageId })
          ownerFullName =
            ((thisPage?.owner?.displayName ?? '').length > 0
              ? thisPage?.owner?.displayName
              : thisPage?.owner?.email) ?? ''
          ownerEntityId = thisPage?.owner?.ithacaId ?? ''
        }
      } else if (isBatch && batchId && !isDemo) {
        const batchPermissions = await getBatchPermissions(
          {
            batchId,
            scopePermissions: false
          },
          token ?? ''
        )
        entityPermissions = batchPermissions?.batch?.entityPermissions
        existingCollaborators = [
          ...getSelectedCollborators(batchPermissions?.batch?.entityPermissions)
        ]
        isOwner = batchPermissions?.batch?.batchOwner.isOwner
        ownerFullName = batchPermissions?.batch?.batchOwner.user.fullName ?? ''
        ownerEntityId = batchPermissions?.batch?.batchOwner.user.ithacaId ?? ''
      } else if (directoryId) {
        const directoryPermissions = await getDirectoryPermissions({
          directoryId,
          scopePermissions: false
        })
        entityPermissions = directoryPermissions.directory?.entityPermissions
        existingCollaborators = [
          ...getSelectedCollborators(directoryPermissions.directory?.entityPermissions)
        ]
        ownerFullName = directoryPermissions.directory?.user.fullName
        isOwner = Boolean(directoryPermissions.directory?.isOwner)
        ownerEntityId = directoryPermissions?.directory?.user.ithacaId ?? ''
      } else if (pageTemplateId) {
        const { getAssetDetails, template } = await getPageTemplatePermissions(
          pageTemplateId,
          token ?? ''
        )
        entityPermissions =
          getAssetDetails?.[0]?.entityPermissions?.map(entityPermission => ({
            ...entityPermission,
            entityName: entityPermission.entityName ?? ''
          })) ?? []
        existingCollaborators = [...getSelectedCollborators(entityPermissions)]
        ownerFullName = (template?.owner?.displayName || template?.owner?.email) ?? ''
        isOwner = myEntityId === template?.owner?.ithacaId ?? ''
        ownerEntityId = template?.owner?.ithacaId ?? ''
      } else if (subdomainId) {
        //Whitelabel Subdomain permissions
        if (isBatch) {
          const result = await getFlowcodeSubdomainPermissions(subdomainId ?? '')
          entityPermissions = (result?.entityPermissions ?? []).map(
            (entityPerm: EntityPermissionsType) => {
              if (entityPerm) {
                return {
                  ...entityPerm,
                  entityType: Number(entityPerm.entityType)
                } as EntityPermissionsType
              }
            }
          )
          existingCollaborators = [...getSelectedCollborators(entityPermissions)]
          isOwner = true
        } else {
          const result = await getFlowpageSubdomainPermissions(subdomainId ?? '')
          entityPermissions = (result?.entityPermissions ?? []).map(
            (entityPerm: EntityPermissionsType) => {
              if (entityPerm) {
                return {
                  ...entityPerm,
                  entityType: Number(entityPerm.entityType)
                } as EntityPermissionsType
              }
            }
          )
          existingCollaborators = [...getSelectedCollborators(result.entityPermissions)]
          isOwner = true
        }
      }
      const orgCollaborator = existingCollaborators.find(
        item => Number(item.entityType) === EntityTypes.ORG
      )
      if (isMounted()) {
        setHasOrgEntity(Boolean(orgCollaborator))
        setOrgEntityAccess(orgCollaborator?.access)
        setExistingCollaborators([...existingCollaborators])
        setOwnerData({
          ownerEntityId,
          ownerFullName,
          isOwner,
          entityPermissions
        })
      }
    } catch (e) {
      console.error(e)
      if (isMounted()) setError(e.message)
    } finally {
      if (isMounted()) setLoading(false)
    }
  }, [
    batchId,
    directoryId,
    isBatch,
    isDemo,
    isMounted,
    pageId,
    thisPage?.owner?.displayName,
    thisPage?.owner?.email,
    thisPage?.owner?.ithacaId,
    token,
    pageTemplateId,
    myEntityId,
    subdomainId
  ])

  useEffect(() => {
    const hasAssetId = Boolean(batchId || directoryId || pageId || subdomainId || pageTemplateId)
    if (shouldFetch && isMounted() && hasAssetId) {
      fetchPermissions()
    }
  }, [
    batchId,
    directoryId,
    isBatch,
    isMounted,
    pageId,
    myEntityId,
    isDemo,
    fetchPermissions,
    shouldFetch,
    pageTemplateId,
    subdomainId
  ])

  // TODO - FP needs a dedicated util until the page owner is returned on the page object
  const permissionName = useMemo(
    () => (isOwner ? PermissionsTypes.CREATOR : getPermissionsName(isOwner, entityPermissions)),
    [isOwner, entityPermissions]
  )
  const viewOnly = pageId ? !(isOwner || permissionName === PermissionsTypes.EDITOR) : !isOwner

  const allPermissions = useMemo<AssetPermission[]>(() => {
    const ownerPermission: AssetPermission = {
      entityId: ownerEntityId,
      entityName: ownerFullName,
      entityType: EntityTypes.USER,
      permissionName: PermissionName.OWNER
    }

    return [
      ownerPermission,
      ...existingCollaborators.map(c => ({
        entityId: c.entityId,
        entityName: c.name,
        entityType: typeof c.entityType === 'string' ? parseInt(c.entityType) : c.entityType,
        permissionName: ASSET_PERMISSIONS_BY_ACCESS_TYPE[c.access as CollaboratorAccess]
      }))
    ]
  }, [existingCollaborators, ownerEntityId, ownerFullName])

  return {
    allPermissions,
    existingCollaborators,
    hasOrgEntity,
    orgEntityAccess,
    // TODO - rename to formatted permission name - DF
    permissionName,
    ownerEntityId,
    ownerFullName,
    viewOnly,
    isOwner,
    loading,
    error,
    entityPermissions,
    fetchPermissions,
    isPageUnpublished
  }
}
